/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, Tooltip } from '@mui/material';
import { useContext, type ReactNode } from 'react';

import { type SectionTab, tabs } from '../../../constants/tabs';
import { GTMContext } from '../../../contexts/GTMContext';
import type { PluginSelectionMode } from '../../PluginSelection/types';
import { GTM_EVENT_BACK, GTM_EVENT_NEXT } from '../../../constants/GTMEventNames';

export type DrawerButtonProps = {
	activeTab: SectionTab;
	featuresView: PluginSelectionMode;
	onTabChange: ( newValue: number ) => void;
	isNavigationDisabled: boolean;
};

export function DrawerButtons( { activeTab, onTabChange, featuresView, isNavigationDisabled }: DrawerButtonProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	function handleBack() {
		const newTabIndex = activeTab.index - 1;
		const newTab = tabs.find( tab => newTabIndex === tab.index )!;

		onTabChange( newTabIndex );

		triggerGTMEvent( {
			event: GTM_EVENT_BACK,
			stage: newTab.tabName,
			view: newTab.tabName === 'Features' ? featuresView : undefined
		} );
	}

	function handleNext() {
		const newTabIndex = activeTab.index + 1;
		const newTab = tabs.find( tab => newTabIndex === tab.index )!;

		onTabChange( newTabIndex );

		triggerGTMEvent( {
			event: GTM_EVENT_NEXT,
			stage: newTab.tabName,
			view: newTab.tabName === 'Features' ? featuresView : undefined
		} );
	}

	return (
		<Box sx={ { display: 'flex', justifyContent: 'space-between', padding: '12px 24px', paddingTop: '8px' } }>
			{ activeTab.index > 0 ?
				<Button
					sx={ { fontWeight: '400', lineHeight: '22px', fontSize: '14px' } }
					variant={ 'text' }
					onClick={ handleBack }
					startIcon={ <ChevronLeft/> }
				>
					Back
				</Button> :
				<div></div>
			}
			{ activeTab.index < tabs.at( -1 )!.index ?
				<Tooltip
					data-testid={ 'drawer-button-next' }
					disableHoverListener={ !isNavigationDisabled }
					title="Select one of the presets above in order to move to the next step."
				>
					<span>
						<Button
							sx={ { fontWeight: '400', lineHeight: '22px', fontSize: '14px' } }
							variant={ 'contained' }
							onClick={ handleNext }
							endIcon={ <ChevronRight/> }
							disabled={ isNavigationDisabled }
						>
							Next
						</Button>
					</span>
				</Tooltip> :
				<span></span>
			}
		</Box>
	);
}
